import styled from 'styled-components';

import {
  CloseButton as PriceTableCloseButton,
  Header as PriceTableHeader,
  Modal as PriceTableModal,
  ModalContent as PriceTableModalContent,
  Overlay as PriceTableOverlay,
  PackageSection as PriceTablePackageSection,
  PriceCard as PriceTablePriceCard,
  PriceCardsContainer as PriceTablePriceCardsContainer,
  PriceInfo as PriceTablePriceInfo,
  PriceLabel as PriceTablePriceLabel,
  PriceNote as PriceTablePriceNote,
  PriceOriginal as PriceTablePriceOriginal,
  PriceTitle as PriceTablePriceTitle,
  PriceValue as PriceTablePriceValue,
  Separator as PriceTableSeparator,
  Title as PriceTableTitle,
} from '@components/views/tour/components/price-table-modal';
import { SectionHeading } from '@shared/SectionHeading';

export const PackageTemplatePriceTableModalShell = styled.div`
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.TABLET})`} {
    ${PriceTableOverlay} {
      background: ${({ theme }) => theme.palette.white};
      align-items: stretch;
      justify-content: stretch;
    }

    ${PriceTableModal} {
      display: flex;
      width: 100vw;
      height: 100vh;
      height: 100dvh;
      max-width: none;
      max-height: none;
      overflow: hidden;
      border-radius: 0;
      box-shadow: none;
    }

    ${PriceTableModalContent} {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 0 16px 24px;
      gap: 0;
    }

    ${PriceTableHeader} {
      position: sticky;
      top: 0;
      z-index: 1;
      min-height: 64px;
      flex: 0 0 auto;
      justify-content: center;
      padding: 16px 40px;
      background: ${({ theme }) => theme.palette.white};
      border-bottom: 1px solid ${({ theme }) => theme.palette.grey300};
    }

    ${PriceTableCloseButton} {
      position: absolute;
      left: 4px;
      order: -1;
    }

    ${PriceTableTitle} {
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
    }

    ${PriceTablePackageSection} {
      min-height: 60px;
      padding: 10px 0;
    }

    ${PriceTablePriceCardsContainer} {
      flex-direction: column;
      overflow-x: visible;
    }

    ${PriceTablePriceCard} {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      min-width: 0;
      min-height: 0;
      padding: 14px 4px;
      align-items: start;
      column-gap: 12px;
    }

    ${PriceTablePriceInfo} {
      display: contents;
    }

    ${PriceTablePriceTitle} {
      grid-column: 1;
      grid-row: 1 / span 4;
      margin-bottom: 0;
      align-self: center;
    }

    ${PriceTablePriceOriginal},
    ${PriceTablePriceValue},
    ${PriceTablePriceNote},
    ${PriceTablePriceLabel} {
      grid-column: 2;
      text-align: right;
    }

    ${PriceTablePriceLabel} {
      margin-top: 0;
    }

    ${PriceTableSeparator} {
      width: 100%;
      height: 1px;
    }
  }
`;

export const Container = styled.section`
  width: 100%;
  padding: 0 0 24px;
  background: transparent;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    padding: 0 0 16px;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  gap: 16px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    grid-template-columns: 1fr;
  }
`;

export const SearchInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.grey300};
  border-radius: 8px;
  padding: 16px;
`;

export const SelectionPanel = styled(Panel)<{ $embedded?: boolean }>`
  ${({ $embedded }) =>
    $embedded
      ? `
          border: 0;
          border-radius: 0;
          padding: 0;
        `
      : ''}
`;

export const PanelTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const SearchInfoPanel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

export const SearchInfoTitle = styled(SectionHeading)`
  width: fit-content;
  margin-bottom: 16px;
`;

export const SearchInfoCard = styled(Panel)`
  flex: 1 1 auto;
  border: 0;
  background: ${({ theme }) => theme.palette.white};
  box-shadow: 0 0 8px rgba(0, 17, 68, 0.08);
`;

export const SelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DropdownSurface = styled.div<{ $width: number }>`
  width: ${({ $width }) => `${$width}px`};
  max-width: calc(100vw - 32px);
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.palette.grey300};
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.white};
  box-shadow: 0 12px 32px rgba(0, 17, 68, 0.16);
  max-height: min(380px, calc(100vh - 32px));
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.palette.grey400} transparent`};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: ${({ theme }) => theme.palette.grey400};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.palette.grey500};
  }
`;

export const MobileModalSurface = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  flex-direction: column;
  background: ${({ theme }) => theme.palette.white};
`;

export const MobileModalHeader = styled.header`
  position: relative;
  display: flex;
  min-height: 64px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 16px 56px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey300};
`;

export const MobileModalCloseButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`;

export const MobileModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.palette.grey1100};
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
`;

export const MobileModalBody = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 16px 24px;
`;

export const MobileSelectionList = styled(SelectionList)`
  gap: 0;
`;

export const SelectionButton = styled.button<{ $selected?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-height: 60px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.primaryBrand : theme.palette.grey300};
  background: ${({ theme, $selected }) =>
    $selected ? theme.palette.grey100 : theme.palette.white};
  color: ${({ theme }) => theme.palette.grey1100};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.72;
  }
`;

export const MobileSelectionButton = styled(SelectionButton)`
  min-height: 50px;
  padding: 12px 0;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey300};
  border-radius: 0;
  background: ${({ theme }) => theme.palette.white};

  &:disabled {
    opacity: 1;
  }
`;

export const SelectionTrigger = styled(SelectionButton)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  min-width: 0;
`;

export const SelectionChevron = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-self: center;
  margin-left: 8px;
`;

export const SelectionContentStack = styled.span`
  min-width: 0;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  overflow: hidden;
  text-align: left;
`;

export const SelectionField = styled(SelectionContentStack)``;

export const SelectionFieldLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: ${({ theme }) => theme.palette.grey700};
`;

export const DisabledSelectionCard = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-height: 60px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.grey300};
  background: ${({ theme }) => theme.palette.grey100};
  color: ${({ theme }) => theme.palette.grey700};
  opacity: 0.72;
`;

export const SelectionLabel = styled.span`
  min-width: 0;
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  text-align: left;
`;

export const DropdownPackageTitle = styled(SelectionLabel)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  overflow-wrap: anywhere;
`;

export const SelectionDate = styled(SelectionLabel)`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SelectionMeta = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.grey1100};
  white-space: nowrap;
`;

export const SelectionPriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

export const SelectionTriggerPriceBlock = styled(SelectionPriceBlock)`
  align-items: flex-start;
`;

export const SelectionTriggerPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
`;

export const SelectionPriceNote = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.grey700};
  white-space: nowrap;
`;

export const Placeholder = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.grey700};
`;

export const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SearchControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 16px;
`;

export const SearchLoadingControls = styled(SearchControls)`
  margin-bottom: 0;
`;

export const SearchInfoDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ValueText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const SummaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

export const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MetaTitle = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.grey700};
`;

export const MetaValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const HighlightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const HighlightsShowMoreButton = styled.button`
  display: block;
  padding: 0;
  margin: 10px 0 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.palette.clearBlue};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

export const HighlightItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const HighlightBullet = styled.span`
  width: 4px;
  height: 4px;
  margin-top: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.palette.primaryBrand};
  flex-shrink: 0;
`;

export const HighlightText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grey1100};
  line-height: 18px;
`;

export const SearchDepartureTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const SearchDepartureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SearchDepartureItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const SearchDepartureName = styled.span`
  font-weight: 400;
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const PackageUtilityRow = styled(ActionRow)`
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  min-height: 42px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    min-height: 0;

    &:empty {
      display: none;
    }
  }
`;

export const ReservationActionsRow = styled(ActionRow)`
  grid-column: 2;
  justify-self: end;
  padding-top: 0;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    grid-column: 1;
    justify-self: stretch;
  }
`;

export const MobileActionRow = styled(ActionRow)`
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

export const MobileMetaText = styled(ValueText)`
  font-weight: 700;
`;

export const GuestPanel = styled(Panel)<{ $embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${({ $embedded }) =>
    $embedded
      ? `
          border: 0;
          border-radius: 0;
          padding: 0;
        `
      : ''}
`;

export const GuestPopoverContent = styled(GuestPanel)<{ $width: number }>`
  width: ${({ $width }) => `${$width}px`};
  max-width: calc(100vw - 32px);
  padding: 12px;
  background: ${({ theme }) => theme.palette.white};
  box-shadow: 0 12px 32px rgba(0, 17, 68, 0.16);
`;

export const GuestRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

export const GuestInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const GuestTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const GuestSubtitle = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grey700};
`;

export const ChildDatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const GuestInfoMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.blue100};
  color: ${({ theme }) => theme.palette.grey1100};
  font-size: 13px;
  line-height: 18px;
`;

export const VariationCard = styled(Panel)`
  border-radius: 8px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, auto);
  column-gap: 24px;
  row-gap: 12px;
  align-items: start;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    grid-template-columns: minmax(0, 1fr);
    column-gap: 0;
  }
`;

export const VariationHeader = styled.div`
  grid-column: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

export const VariationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const VariationTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const VariationDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.grey700};
`;

export const VariationPriceBlock = styled.div`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    grid-column: 1;
    grid-row: auto;
    align-items: flex-start;
    text-align: left;
  }
`;

export const VariationPriceNote = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grey700};
`;

export const TryEquivalentPrice = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const DeparturePointsBlock = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DeparturePointsTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const DeparturePointsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DeparturePointsItem = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grey700};
`;

export const WarningText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.primaryBrand};
  font-weight: 600;
`;

export const ReserveButton = styled.button`
  min-width: 180px;
  padding: 12px 16px;
  border: 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.primaryBrand};
  color: ${({ theme }) => theme.palette.white};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    width: 100%;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const FullPackageCallButton = styled.button`
  grid-column: 1 / -1;
  justify-self: end;
  min-width: 180px;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.palette.primaryBrand};
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.primaryBrand};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    width: 100%;
  }
`;

export const OccupiedAvailabilityBox = styled.div`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  min-width: 220px;
  min-height: 70px;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.hesInfoBoxBackground};

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    grid-column: 1;
    grid-row: auto;
    width: 100%;
    min-width: 0;
  }
`;

export const OccupiedAvailabilityText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.palette.orange500};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
`;

export const SelectableItemContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

export const OccupiedBadge = styled.span`
  display: inline-flex;
  margin-top: 4px;
  padding: 4px;
  justify-content: flex-start;
  align-items: center;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.orange50};
  color: ${({ theme }) => theme.palette.orange500};
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
  white-space: nowrap;
`;

export const DropdownRemainingQuotaBadge = styled.span`
  display: inline-flex;
  margin-top: 4px;
  padding: 4px;
  justify-content: flex-start;
  align-items: center;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.red100};
  color: ${({ theme }) => theme.palette.red700};
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
  white-space: nowrap;
`;
